(() => {
  const SELECTOR = '[data-al-world]';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const mobileLayout = window.matchMedia('(max-width: 749px)');

  const initWorld = (root) => {
    if (!root || root.dataset.worldReady === 'true') return;
    root.dataset.worldReady = 'true';

    const camera = root.querySelector('[data-world-camera]');
    const portals = [...root.querySelectorAll('[data-world-portal]')];
    const mobileCards = [...root.querySelectorAll('[data-world-mobile-card]')];
    const mobileReveals = [...root.querySelectorAll('[data-world-mobile-reveal]')];
    if (!camera || portals.length !== 3) return;

    const controller = new AbortController();
    const { signal } = controller;
    let frame = 0;
    let currentX = 0;
    let currentY = 0;
    let targetX = 0;
    let targetY = 0;
    let lastFrame = performance.now();
    let slowFrames = 0;
    let visible = !document.hidden;
    let mobileFrame = 0;
    let revealObserver;

    const render = (now) => {
      if (!visible || reduceMotion.matches || !finePointer.matches) {
        frame = 0;
        return;
      }

      const delta = now - lastFrame;
      lastFrame = now;
      slowFrames = delta > 30 ? slowFrames + 1 : Math.max(0, slowFrames - 1);
      if (slowFrames > 26) root.classList.add('is-low-performance');

      currentX += (targetX - currentX) * .055;
      currentY += (targetY - currentY) * .055;
      root.style.setProperty('--world-x', currentX.toFixed(4));
      root.style.setProperty('--world-y', currentY.toFixed(4));

      if (Math.abs(targetX - currentX) > .001 || Math.abs(targetY - currentY) > .001) {
        frame = requestAnimationFrame(render);
      } else {
        frame = 0;
      }
    };

    const requestRender = () => {
      if (!frame && visible && !reduceMotion.matches && finePointer.matches) {
        lastFrame = performance.now();
        frame = requestAnimationFrame(render);
      }
    };

    const revealMobileContent = () => {
      const revealItems = [...mobileReveals, ...mobileCards];
      if (reduceMotion.matches || !('IntersectionObserver' in window)) {
        revealItems.forEach((item) => item.classList.add('is-visible'));
        return;
      }

      revealObserver?.disconnect();
      revealObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      }, {
        rootMargin: '0px 0px -8% 0px',
        threshold: .08
      });

      revealItems.forEach((item) => revealObserver.observe(item));
    };

    const updateMobileParallax = () => {
      mobileFrame = 0;
      if (!visible || reduceMotion.matches || !mobileLayout.matches) return;

      const viewport = window.innerHeight || document.documentElement.clientHeight;
      mobileCards.forEach((card) => {
        const bounds = card.getBoundingClientRect();
        if (bounds.bottom < 0 || bounds.top > viewport) return;
        const distance = (bounds.top + bounds.height / 2 - viewport / 2) / (viewport + bounds.height);
        const parallax = Math.max(-1, Math.min(1, distance * 2.6));
        card.style.setProperty('--mobile-parallax', parallax.toFixed(3));
      });
    };

    const requestMobileParallax = () => {
      if (!mobileFrame && visible && !reduceMotion.matches && mobileLayout.matches) {
        mobileFrame = requestAnimationFrame(updateMobileParallax);
      }
    };

    const setActivePortal = (portal) => {
      const key = portal?.dataset.worldPortal;
      if (key) {
        root.dataset.activePortal = key;
        const bias = key === 'library' ? -0.32 : key === 'craftsmanship' ? .32 : 0;
        targetX = Math.max(-1, Math.min(1, targetX + bias));
        requestRender();
      } else {
        delete root.dataset.activePortal;
      }
    };

    root.addEventListener('pointermove', (event) => {
      if (!finePointer.matches || reduceMotion.matches) return;
      const bounds = root.getBoundingClientRect();
      targetX = ((event.clientX - bounds.left) / bounds.width - .5) * 2;
      targetY = ((event.clientY - bounds.top) / bounds.height - .5) * 2;
      requestRender();
    }, { passive: true, signal });

    root.addEventListener('pointerleave', () => {
      targetX = 0;
      targetY = 0;
      delete root.dataset.activePortal;
      requestRender();
    }, { passive: true, signal });

    portals.forEach((portal) => {
      portal.addEventListener('pointerenter', () => setActivePortal(portal), { signal });
      portal.addEventListener('focus', () => setActivePortal(portal), { signal });
      portal.addEventListener('blur', () => setActivePortal(null), { signal });
      portal.addEventListener('click', (event) => {
        const modified = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0;
        if (modified || reduceMotion.matches || !finePointer.matches) return;

        event.preventDefault();
        portals.forEach((item) => delete item.dataset.isEntering);
        portal.dataset.isEntering = 'true';
        root.style.setProperty('--world-entry-x', `${portal.offsetLeft + portal.offsetWidth / 2}px`);
        root.style.setProperty('--world-entry-y', `${portal.offsetTop + portal.offsetHeight / 2}px`);
        root.classList.add('is-entering');
        window.setTimeout(() => window.location.assign(portal.href), 820);
      }, { signal });
    });

    window.addEventListener('scroll', requestMobileParallax, { passive: true, signal });
    window.addEventListener('resize', requestMobileParallax, { passive: true, signal });

    document.addEventListener('visibilitychange', () => {
      visible = !document.hidden;
      if (!visible && frame) {
        cancelAnimationFrame(frame);
        frame = 0;
      } else if (visible) {
        requestRender();
        requestMobileParallax();
      }
    }, { signal });

    const motionChanged = () => {
      if (reduceMotion.matches) {
        targetX = currentX = 0;
        targetY = currentY = 0;
        root.style.setProperty('--world-x', '0');
        root.style.setProperty('--world-y', '0');
        [...mobileReveals, ...mobileCards].forEach((item) => item.classList.add('is-visible'));
      }
      requestRender();
      requestMobileParallax();
    };

    const mobileLayoutChanged = () => {
      revealMobileContent();
      requestMobileParallax();
    };

    reduceMotion.addEventListener('change', motionChanged, { signal });
    finePointer.addEventListener('change', motionChanged, { signal });
    mobileLayout.addEventListener('change', mobileLayoutChanged, { signal });

    root.classList.add('is-enhanced');
    revealMobileContent();
    requestMobileParallax();
    root.worldCleanup = () => {
      controller.abort();
      if (frame) cancelAnimationFrame(frame);
      if (mobileFrame) cancelAnimationFrame(mobileFrame);
      revealObserver?.disconnect();
    };
  };

  const initAll = (scope = document) => scope.querySelectorAll(SELECTOR).forEach(initWorld);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.querySelector(SELECTOR);
    root?.worldCleanup?.();
  });
})();
